import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['filterCount', 'input', 'spinner', 'frame', 'applyButton']

  static values = {
    filters: Array,
    turboFrameId: String,
    singleSelectFilters: Array,
    defaultValues: Object
  }

  connect() {
    this.storeInitialState()
    this.updateApplyButtonState()
    this.updateCounters()
  }

  inputChanged() {
    this.updateApplyButtonState()
  }

  formSubmitted() {
    this.requestIdleCallback(() => {
      this.storeInitialState()
      this.updateApplyButtonState()
    })
  }

  turboFrameLoaded(event) {
    this.updateCounters()

    if (this.hasTurboFrameIdValue && event.target.id === this.turboFrameIdValue) {
      this.storeInitialState()
      this.updateApplyButtonState()
    }

    this.hideSpinner()
  }

  storeInitialState() {
    this.initialInputStates = new Map()
    this.inputTargets.forEach(input => {
      this.initialInputStates.set(input, this.getInputValue(input))
    })
  }

  updateApplyButtonState() {
    if (!this.hasApplyButtonTarget) return

    const hasChanges = this.hasInputChanges()
    this.applyButtonTarget.disabled = !hasChanges
  }

  hasInputChanges() {
    return this.inputTargets.some(input => {
      const initialValue = this.initialInputStates.get(input)
      const currentValue = this.getInputValue(input)
      return currentValue !== initialValue
    })
  }

  getInputValue(input) {
    switch (input.type) {
      case 'checkbox':
      case 'radio':
        return input.checked
      case 'select-multiple':
        return Array.from(input.selectedOptions).map(option => option.value).join(',')
      default:
        return input.value
    }
  }

  updateCounters() {
    this.requestIdleCallback(() => {
      const url = this.getTurboFrameUrl()

      this.filtersValue.forEach((filter) => {
        const isSingleSelect = this.singleSelectFiltersValue.includes(filter)
        const params = isSingleSelect
          ? url.searchParams.getAll(filter)
          : url.searchParams.getAll(`${filter}[]`)
        const target = this.filterCountTargets.find((t) => t.dataset.filter === filter)

        // Check if filter value differs from its default (e.g., "all" is the default, so no counter shown)
        const defaultValue = this.defaultValuesValue?.[filter]
        const isActive = isSingleSelect
          ? params.length > 0 && params[0] !== defaultValue
          : params.length > 0

        if (isActive && target) {
          const count = isSingleSelect ? 1 : params.length
          this.showFilterCount(target, count)
        } else if (target) {
          this.hideFilterCount(target)
        }
      })
    })
  }

  showFilterCount(target, count) {
    const span = target.querySelector('span')
    if (span) {
      span.innerText = count
      span.classList.remove('hidden', 'absolute', 'opacity-0')
    }
  }

  hideFilterCount(target) {
    const span = target.querySelector('span')
    if (span) {
      span.classList.add('hidden')
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add('hidden')
    }
  }

  // Everything the filters hold, the search box beside them included: the form
  // sits outside the frame it reloads, so nothing else empties it.
  clearAll() {
    this.uncheckAll()
    this.resetSingleSelects()
    this.element.querySelectorAll('input[type="text"]').forEach(input => { input.value = '' })
  }

  // Back to the default the cleared page shows, so the next submit doesn't bring the old choice back.
  resetSingleSelects() {
    this.inputTargets
      .filter(input => input.type === 'radio')
      .forEach(radio => {
        radio.checked = radio.value === this.defaultValuesValue?.[radio.name]
      })
  }

  uncheckAll() {
    this.inputTargets
      .filter(input => input.type === 'checkbox')
      .forEach(checkbox => {
        checkbox.checked = false
      })
  }

  clearFilter(event) {
    const dropdown = event.target.closest('[data-controller*="dropdown"]')

    if (dropdown) {
      const inputs = dropdown.querySelectorAll('[data-form-target="input"]')

      inputs.forEach(input => {
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = false
        } else {
          input.value = ''
        }
      })

      this.updateApplyButtonState()
    }
  }

  clearSpecificFilter(event) {
    event.preventDefault()
    event.stopPropagation()

    const filterKey = event.currentTarget.dataset.filterKey
    const dropdown = event.currentTarget.closest('[data-controller*="dropdown"]')

    if (dropdown && filterKey) {
      const isSingleSelect = this.singleSelectFiltersValue.includes(filterKey)
      const selector = isSingleSelect
        ? `input[name="${filterKey}"]`
        : `input[name="${filterKey}[]"]`

      const inputs = dropdown.querySelectorAll(selector)

      inputs.forEach(input => {
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = false
        } else {
          input.value = ''
        }
      })

      this.updateApplyButtonState()

      const form = this.element.querySelector('form')
      form.requestSubmit()
    }
  }

  getTurboFrameUrl() {
    const frameUrl = this.turboFrame?.getAttribute('src')
    return frameUrl ? new URL(frameUrl) : new URL(window.location.href)
  }

  requestIdleCallback(callback) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(callback)
    } else {
      setTimeout(callback, 100)
    }
  }

  get turboFrame() {
    if (this.hasFrameTarget) {
      return this.frameTarget
    }

    if (this.hasTurboFrameIdValue) {
      return document.querySelector(`turbo-frame#${this.turboFrameIdValue}`)
    }
  }
}
